import React, { useState, useEffect } from 'react';
import cn from 'clsx';
import s from './Toolbar.module.css';
import { useIntersectionObserver } from '../../../lib/hooks/use-intersection-observer';

interface ToolbarProps {
    children: React.ReactNode;
    shouldStick: boolean;
    position: 'top' | 'bottom';
    variant?: 'headerNav' | 'filteringToolbar';
    targetRef?: React.RefObject<Element>;
    initiallyHidden?: boolean;
    positioningMode?: 'sticky' | 'fixed';
}

const Toolbar = ({
    children,
    shouldStick = true,
    position = 'top',
    variant,
    targetRef,
    initiallyHidden = false,
    positioningMode = 'sticky',
}: ToolbarProps) => {
    const [isSticking, setIsSticking] = useState(false);
    const [isVisible, setIsVisible] = useState(!initiallyHidden);
    // The sentinel ref is for when the Toolbar needs to observe itself (e.g. when the condition for its sticking is when it itself comes into view)
    const [sentinelRef, sentinelEntry] = useIntersectionObserver({
        threshold: 0,
    });
    // The target observer ref is for when the Toolbar needs to do something based on another DOM element (e.g. when the condition for its sticking is when a DOM element other than itself comes in or out of view)
    const [targetObserverRef, targetEntry] = useIntersectionObserver({
        threshold: 0,
        rootMargin: '96px',
    });

    useEffect(() => {
        if (shouldStick && sentinelEntry) {
            setIsSticking(!sentinelEntry.isIntersecting);
        }
    }, [sentinelEntry, shouldStick]);

    useEffect(() => {
        if (targetRef?.current) {
            targetObserverRef(targetRef.current);
        }
    }, [targetRef, targetObserverRef]);

    useEffect(() => {
        if (targetEntry) {
            setIsVisible(!targetEntry.isIntersecting);
        }
    }, [targetEntry]);

    const classes = cn(s.root, {
        [s.sticky]: positioningMode === 'sticky' && shouldStick,
        [s.fixed]: positioningMode === 'fixed' && shouldStick,
        [s.top]: position === 'top' && shouldStick,
        [s.bottom]: position === 'bottom' && shouldStick,
        [s.isSticking]: isSticking,
        [s.headerNav]: variant === 'headerNav',
        [s.hidden]: initiallyHidden && !isVisible,
        [s.visible]: initiallyHidden && isVisible,
    });

    return (
        <>
            <div ref={sentinelRef} className={s.sentinel} />
            <div className={cn(s.root, classes)}>{children}</div>
        </>
    );
};

export default Toolbar;
